import { createFileRoute, Link } from '@tanstack/react-router'
import { ContactForm } from '@/components/ContactForm'
import { CONTACT_EMAIL, CONTACT_MAILTO_URL } from '@/lib/constants'
import { SITE_IMAGES } from '@/lib/site-images'

export const Route = createFileRoute('/')({
  head: () => ({
    meta: [
      { title: 'Halo Compliance — Mortgage Marketing Compliance AI' },
      {
        name: 'description',
        content:
          'Mortgage marketing compliance — AI fast. Upload collateral for instant TILA, UDAAP, and RESPA feedback.',
      },
      { property: 'og:title', content: 'Halo Compliance — Mortgage Marketing Compliance AI' },
      { property: 'og:url', content: 'https://www.halocompliance.com/' },
      { property: 'og:image', content: 'https://www.halocompliance.com/images/home/hero.jpg' },
    ],
    links: [{ rel: 'canonical', href: 'https://www.halocompliance.com/' }],
  }),
  component: HomePage,
})

const heroBg = `linear-gradient(165deg, rgba(26,26,26,0.88) 0%, rgba(37,37,37,0.82) 50%, rgba(45,45,45,0.9) 100%), url("${SITE_IMAGES.home.hero}")`

const bandBg = `linear-gradient(rgba(22,22,22,0.76), rgba(22,22,22,0.84)), url("${SITE_IMAGES.home.band}")`

function HomePage() {
  return (
    <>
      <section
        className="marketing-hero-photo text-white py-5 py-md-6"
        style={{
          backgroundImage: heroBg,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center">
              <h1 className="display-4 fw-normal font-serif mb-3">
                Mortgage marketing compliance…
              </h1>
              <h1 className="display-4 fw-normal font-serif mb-4 text-warning">AI fast.</h1>
              <p className="lead text-white-75 mb-4 px-md-5">
                Upload your collateral. Get instant TILA, UDAAP, and RESPA compliance feedback — built
                for mortgage teams.
              </p>
              <Link to="/contact" className="btn btn-primary btn-lg px-5">
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-body-secondary bg-opacity-50">
        <div className="container py-4">
          <div className="text-center mb-5">
            <h2 className="h3 font-serif mb-3">AI powered compliance review in seconds.</h2>
          </div>
          <div className="row g-4 row-cols-1 row-cols-md-2 row-cols-lg-4">
            <StepCard
              step={1}
              title="Build your compliance rulebook, or use ours."
              body="Custom disclosures and disclaimers, jurisdiction specific."
              imageSrc={SITE_IMAGES.home.stepRulebook}
              imageAlt="Compliance rulebook in Halo"
            />
            <StepCard
              step={2}
              title="Upload your marketing collateral."
              imageSrc={SITE_IMAGES.home.stepInput}
              imageAlt="Upload marketing collateral"
            />
            <StepCard
              step={3}
              title="Run your marketing collateral through Halo's Compliance Engine."
              imageSrc={SITE_IMAGES.home.stepEngine}
              imageAlt="Halo Compliance Engine"
            />
            <StepCard
              step={4}
              title="Get your compliance report."
              imageSrc={SITE_IMAGES.home.stepReport}
              imageAlt="Compliance report"
            />
          </div>
        </div>
      </section>

      <section
        className="marketing-band-photo py-5 py-md-6 text-white"
        style={{
          backgroundImage: bandBg,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="container py-4">
          <div className="row align-items-center justify-content-center">
            <div className="col-lg-8 text-center">
              <h2 className="h2 font-serif mb-4 text-white">Free your self from the mundane.</h2>
              <p className="lead text-white-75 mb-4">
                Halo's AI powered compliance engine allows you for focus on the critical task of managing
                enterprise risk.
              </p>
              <Link to="/product" className="btn btn-outline-light btn-lg border-2">
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-body border-top border-secondary border-opacity-10">
        <div className="container py-4">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2 className="display-6 font-serif text-center mb-4">Reach out.</h2>
              <p className="lead text-body-secondary text-center mb-4">
                Let Halo Compliance show you why sales, marketing and compliance professionals in the
                mortgage industry love our simple solution.
              </p>
              <p className="text-center mb-4">
                Fill out the form below or email us at:{' '}
                <a href={CONTACT_MAILTO_URL}>{CONTACT_EMAIL}</a>
              </p>
              <ContactForm idPrefix="home-reach-out" />
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

function StepCard({
  step,
  title,
  body,
  imageSrc,
  imageAlt,
}: {
  step: number
  title: string
  body?: string
  imageSrc: string
  imageAlt: string
}) {
  return (
    <div className="col">
      <div className="card h-100 border-0 shadow-sm marketing-step-card overflow-hidden">
        <img
          src={imageSrc}
          alt={imageAlt}
          className="marketing-step-thumb w-100 object-fit-cover"
          loading="lazy"
          decoding="async"
        />
        <div className="card-body p-4">
          <p className="eyebrow mb-2">Step {step}</p>
          <h3 className="h6 mb-2">{title}</h3>
          {body ? <p className="small text-body-secondary mb-0">{body}</p> : null}
        </div>
      </div>
    </div>
  )
}
